import { FC } from "react";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import {
	AgentConfigField,
	AgentConfigFieldType,
} from "../../domain/validationAgent.interface";

/**
 * The bound agent's own configuration — SIMS-8223 AC2.
 *
 * 🔴 Every control here is rendered from the agent's DECLARED schema. Nothing in
 * this file knows what a meal-photo agent or a document-legibility agent needs;
 * that is the whole of "no new UI work per agent" in the story's goal. A new
 * agent ships as a catalog document plus an implementation — never as a new
 * screen — and if this file ever grows a branch on `agentKey`, that promise has
 * been broken.
 *
 * Placement follows the platform pattern Qori confirmed against the code: a peer
 * section in the conditional-config slot of Event maintenance, sibling to
 * PhotoConfigSection, not nested inside the Validation card.
 */

export interface AgentConfigFieldError {
	field: string;
	message: string;
}

interface AgentConfigFieldsProps {
	schema: AgentConfigField[];
	values: Record<string, unknown>;
	errors?: AgentConfigFieldError[];
	disabled?: boolean;
	onChange: (values: Record<string, unknown>) => void;
}

const errorFor = (
	errors: AgentConfigFieldError[] | undefined,
	key: string,
): string | undefined => errors?.find((e) => e.field === key)?.message;

export const AgentConfigFields: FC<AgentConfigFieldsProps> = ({
	schema,
	values,
	errors,
	disabled = false,
	onChange,
}) => {
	const set = (key: string, value: unknown): void =>
		onChange({ ...values, [key]: value });

	return (
		<Box>
			<Box
				sx={{
					display: "flex",
					alignItems: "baseline",
					justifyContent: "space-between",
					mb: 1,
				}}
			>
				<Typography variant="titleSmall">Agent configuration</Typography>
				{/* The frame states the promise on the screen itself, where a
				    configurator wondering "will a new agent need a release?" will
				    actually read it. */}
				<Typography variant="bodySmall" color="text.secondary">
					Rendered from the selected agent&apos;s own schema — adding an agent
					needs no new UI.
				</Typography>
			</Box>

			<Grid container spacing={2}>
				{schema.map((field) => {
					const message = errorFor(errors, field.key);
					const value = values[field.key];

					if (field.type === AgentConfigFieldType.BOOLEAN) {
						return (
							<Grid item xs={12} md={6} key={field.key}>
								<FormControlLabel
									control={
										<Checkbox
											checked={value === true}
											disabled={disabled}
											onChange={(event) => set(field.key, event.target.checked)}
											inputProps={{ "aria-label": field.label }}
										/>
									}
									label={field.label}
								/>
								{(message ?? field.helpText) && (
									<Typography
										variant="bodySmall"
										color={message ? "error" : "text.secondary"}
										sx={{ display: "block" }}
									>
										{message ?? field.helpText}
									</Typography>
								)}
							</Grid>
						);
					}

					const isEnum = field.type === AgentConfigFieldType.ENUM;
					return (
						<Grid item xs={12} md={6} key={field.key}>
							<TextField
								fullWidth
								size="small"
								select={isEnum}
								type={
									field.type === AgentConfigFieldType.NUMBER ? "number" : "text"
								}
								label={field.label}
								required={field.required}
								disabled={disabled}
								error={!!message}
								// The reason always has a home: the field's own error when
								// there is one, otherwise whatever the agent declared as
								// help. Never a tooltip, never colour alone.
								helperText={message ?? field.helpText ?? " "}
								value={value ?? ""}
								onChange={(event) =>
									set(
										field.key,
										field.type === AgentConfigFieldType.NUMBER
											? // "" must stay empty, not become 0 — a cleared field
												// is unset, and 0 is a value someone chose.
												event.target.value === ""
												? undefined
												: Number(event.target.value)
											: event.target.value,
									)
								}
								inputProps={
									field.type === AgentConfigFieldType.NUMBER
										? { min: field.min, max: field.max }
										: undefined
								}
							>
								{isEnum &&
									(field.options ?? []).map((option) => (
										<MenuItem key={option} value={option}>
											{option}
										</MenuItem>
									))}
							</TextField>
						</Grid>
					);
				})}
				{/* 🔴 G31 — `On agent error` is NOT configurable and NOT from the
				    agent's schema. F6 fixes it to coordinator review and the design
				    renders it read-only with a lock. An earlier Hi-Fi cut showed a
				    dropdown; the ticket says explicitly not to build it. It lives
				    here, inside Agent configuration, because that is where a
				    configurator looks for it — and finding nothing would read as a
				    missing setting rather than a fixed one. */}
				<Grid item xs={12} md={6}>
					<Typography
						variant="bodySmall"
						color="text.secondary"
						sx={{ display: "block", mb: 0.5 }}
					>
						On agent error
					</Typography>
					<Box
						sx={{
							p: 1.5,
							borderRadius: 1,
							bgcolor: "action.hover",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<LockOutlinedIcon fontSize="small" color="disabled" />
						<Typography variant="bodyMedium">
							Send to coordinator review
						</Typography>
					</Box>
					<Typography
						variant="bodySmall"
						color="text.secondary"
						sx={{ display: "block", mt: 0.5 }}
					>
						Fixed behaviour. An agent outage degrades to review, never a hard
						block or an auto-approval.
					</Typography>
				</Grid>
			</Grid>
		</Box>
	);
};

export default AgentConfigFields;
